import delegate from "delegate-it";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import ajax from "@/scripts/common/ajax";
import { scrollToIfNeeded } from "@/scripts/common/scroll-to";
import { updateQueryParams } from "@/scripts/common/util";
import "./promotions.css";

const updateResults = async () => {
  const pageContent = document.querySelector("[data-page-content]");

  pageContent.classList.add("is-loading");

  const res = await ajax.get(window.location.href).json();

  pageContent.innerHTML = res.page;
  pageContent.classList.remove("is-loading");

  if (res.isFiltered) {
    scrollToIfNeeded(pageContent, 24);
  }

  setupScrollPixel();
};

const handleViewChange = (e) => {
  const pageContent = document.querySelector("[data-page-content]");

  document.querySelector('[data-change-view][aria-selected="true"]').setAttribute("aria-selected", "false");
  e.delegateTarget.setAttribute("aria-selected", "true");

  const url = updateQueryParams([["view", e.delegateTarget.dataset.changeView]]);

  window.history.replaceState({}, "", url);

  scrollToIfNeeded(pageContent, 24);

  updateResults();
};

const handleLocationChange = (e) => {
  const params = [];

  let location = e.delegateTarget.dataset.changeLocation;

  if (location === "_all_") {
    location = false;
    params.push(["month", false]);
  }

  params.push(["location", location]);

  const url = updateQueryParams(params);

  window.history.replaceState({}, "", url);

  updateResults();
};

const handleMonthChange = (e) => {
  const param = e.delegateTarget.dataset.changeMonth;

  const url = updateQueryParams([["month", param]]);

  window.history.replaceState({}, "", url);

  updateResults();
};

const handleFilterClear = (e) => {
  e.preventDefault();

  const url = updateQueryParams([
    ["location", false],
    ["month", false],
  ]);

  window.history.replaceState({}, "", url);

  updateResults();
};

const handleCategoryFilterChange = () => {
  const form = document.querySelector(".category-filter-form");
  const filterKeys = ["categories"];
  const formData = new FormData(form);
  const selectedFilters = new Map(filterKeys.map((key) => [key, new Set()]));

  for (const [name, value] of formData.entries()) {
    selectedFilters.get(name).add(value);
  }

  const params = [...selectedFilters].map(([key, set]) => [key, set.size ? [...set].join("_") : false]);
  const url = updateQueryParams(params);

  window.history.replaceState({}, "", url);

  updateResults();
};

const handleCategoriesToggle = (e) => {
  const expanded = e.delegateTarget.getAttribute("aria-expanded") === "true";

  const url = updateQueryParams([["browse-categories", expanded ? "true" : false]]);

  window.history.replaceState({}, "", url);
};

const handleCategoriesClear = (e) => {
  e.preventDefault();

  const url = updateQueryParams([["categories", false]]);

  window.history.replaceState({}, "", url);

  updateResults();
};

const handlePromotionModal = async (e) => {
  const trigger = e.delegateTarget;
  const url = trigger.dataset.datePromotion;
  const offsetTop =
    parseInt(window.getComputedStyle(document.documentElement).getPropertyValue("--site-header-stuck-height")) + 20;

  const content = await ajax.get(url).text();

  const instance = tippy(trigger, {
    allowHTML: true,
    appendTo: document.body,
    content,
    arrow: false,
    maxWidth: window.matchMedia("(min-width: 640px)").matches ? 720 : 300,
    interactive: true,
    placement: "top",
    trigger: "manual",
    zIndex: 20,
    popperOptions: {
      modifiers: [
        {
          name: "preventOverflow",
          boundary: document.documentElement,
          options: {
            padding: { top: offsetTop, right: 20, bottom: 20, left: 20 },
          },
        },
      ],
    },
    onShow: async (instance) => {
      const closeBtn = instance.popper.querySelector(".tippy-close");

      if (closeBtn) {
        closeBtn.addEventListener("click", () => {
          instance.hide();
        });
      }

      instance.reference.setAttribute("aria-selected", "true");
    },
    onHide: (instance) => {
      instance.reference.removeAttribute("aria-selected");
    },
  });

  requestAnimationFrame(() => {
    instance.show();
  });
};

const setupScrollPixel = () => {
  const promotionsGrid = document.querySelector(".promotions-grid");
  const scrollPixel = document.querySelector(".promotions-grid-scroll-pixel");

  if (promotionsGrid && scrollPixel) {
    const observer = new IntersectionObserver(
      ([e]) => promotionsGrid.classList.toggle("is-stuck", e.intersectionRatio < 1),
      {
        threshold: [1],
      },
    );

    observer.observe(scrollPixel);
  }
};

document.addEventListener("DOMContentLoaded", () => {
  delegate('[data-change-view]:not([aria-selected="true"])', "click", handleViewChange);
  delegate("[data-change-location]", "click", handleLocationChange);
  delegate("[data-change-month]", "click", handleMonthChange);
  delegate(".filter-form-clear", "click", handleFilterClear);
  delegate(".category-filter-form input", "change", handleCategoryFilterChange);
  delegate("[data-categories-toggle]", "click", handleCategoriesToggle);
  delegate("[data-date-promotion]", "click", handlePromotionModal);
  delegate(".categories-clear", "click", handleCategoriesClear);

  setupScrollPixel();
});
